import React, { useEffect, useMemo } from "react";
import { Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { useSaleRoModel } from "../saleRoModel";
import { ISaleRoDetail } from "../saleRoTypes";
import { useDictModel } from "@/views/base/dict/dictModel";
import YhTag from "@/components/YhTag";
import { momentFormat, formatNum } from "@/utils/utils";
import useNavModel from "@/model/navModel";
import { tableSize } from "@/utils/constants";
import YhTootip from "@/components/YhTootip";
import { roleColumnModel } from "@/utils/roleColumn";
import useGlobalModel from "@/model/globalModel";

type statusType = {
    "sale-return-status.write-off": string,
    "sale-return-status.change": string,
    "sale-return-status.settle": string,
    "sale-return-status.stock-io": string,
    "sale-return-status.create": string,
    [key: string]: any
}
export function DataTable() {
    const {
        dataSource, selectedRowKeys, pgCfg, descRows, rowsDescTotal, descTotal,
        loadData, setSelectedRowKeys, setDescRows, reset
    } = useSaleRoModel();
    const { getTreeNodeName } = useDictModel();
    const { selectedNavId, replaceNav, findNav } = useNavModel();
    const { user: { roles } } = useGlobalModel();
    const { getRoleColumns } = roleColumnModel();

    const onLook = (code: string) => {
        let supplierEditKey = "5-3-3";
        replaceNav(
            selectedNavId,
            supplierEditKey,
            { id: code, mode: "look", },
            true
        );
    }
    useEffect(() => {
        loadData();
    }, [])

    const statusColor: statusType = {
        "sale-return-status.write-off": "green",
        "sale-return-status.change": "red",
        "sale-return-status.settle": "cyan",
        "sale-return-status.stock-io": "green",
        "sale-return-status.create": "blue",
    }
    const defaultColumns: ColumnsType<ISaleRoDetail> = [
        {
            title: '序号',
            fixed: "left",
            width: 60,
            render: (t, r, i) => i + 1
        }, {
            title: '退货单号',
            width: 120,
            dataIndex: 'returnCode',
            render: (text: any) => <YhTootip text={text} onClick={() => onLook(text)} />,
            // render: (text: any) => <Tooltip placement="topLeft" title={text}><p className="ellipsis click-span" onClick={(e) => {e.stopPropagation();onLook(text)}}>{text}</p></Tooltip>,
        },
        {
            title: '原销售单号',
            width: 120,
            dataIndex: 'saleCode',
        }, {
            title: '单据状态',
            width: 80,
            dataIndex: 'status',
            render: (text) => <YhTag color={statusColor[text]} text={getTreeNodeName(text)} />,
        }, {
            title: '支付状态',
            width: 80,
            dataIndex: 'payStatus',
            render: (text) => getTreeNodeName(text)
        }, {
            title: '客户名称',
            width: 150,
            dataIndex: 'customerName',
            render: (text: any) => <YhTootip text={text} />,

        }, {
            title: '联系人',
            width: 150,
            dataIndex: 'contact',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '电话',
            width: 95,
            dataIndex: 'phone',
        }, {
            title: '退货数量',
            width: 100,
            dataIndex: 'totalNum',
        }, {
            title: '退货金额',
            width: 100,
            dataIndex: 'receivedAmount',
            render: (text) => `￥${formatNum(text)}`
        }, {
            title: '已付金额',
            width: 100,
            dataIndex: 'receivableAmount',
            render: (text) => `￥${formatNum(text)}`
        }, {
            title: '退货成本',
            width: 100,
            dataIndex: 'totalCost',
            render: (text) => `￥${formatNum(text)}`
        }, {
            title: '结算方',
            width: 80,
            dataIndex: 'settlementPartyName',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '结算类型',
            width: 80,
            dataIndex: 'payType',
            render: (text) => getTreeNodeName(text)
        }, {
            title: '结算方式',
            width: 160,
            dataIndex: 'payMethodName',
            // render: (text: any) => <YhTootip text={text} />,
            render: (text: any, record: any) => <Tooltip placement="topLeft" title={record?.settleDetails?.[0]?.payMethodName ?? record?.settleDetails?.[0]?.accountName}><p className="ellipsis">{record?.settleDetails?.[0]?.payMethodName ?? record?.settleDetails?.[0]?.accountName}</p></Tooltip>,
        }, {
            title: '销售类型',
            width: 80,
            dataIndex: 'salesTypeName',
            render: text => text === '' || text === null ? '正常销售' : text
        }, {
            title: '发票类型',
            width: 160,
            dataIndex: 'invoiceTypeName',
        }, {
            title: '开票状态',
            width: 80,
            dataIndex: 'invoiceStatus',
            render: (text) => text == "1" ? "已开票" : "未开票"
        }, {
            title: '制单员',
            width: 70,
            dataIndex: 'createUser',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '制单日期',
            width: 130,
            dataIndex: 'createTime',
            render: (text) => momentFormat(text)
        }, {
            title: '库管员',
            width: 70,
            dataIndex: 'storekeeper',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '入库时间',
            width: 130,
            dataIndex: 'stockIoTime',
            render: (text) => momentFormat(text)
        }, {
            title: '退货日期',
            width: 130,
            dataIndex: 'stockIoTime',
            render: (text) => momentFormat(text)
        }, {
            title: '结算日期',
            width: 130,
            dataIndex: 'settleTime',
            render: (text) => momentFormat(text)
        }, {
            title: '结算员',
            width: 80,
            dataIndex: 'reckoner',
        }, {
            title: '核销时间',
            width: 130,
            dataIndex: 'writeOffTime',
            render: (text) => momentFormat(text)
        }, {
            title: '作废原因',
            width: 120,
            dataIndex: 'cancelReason',
        }, {
            title: '财务备注',
            width: 120,
            dataIndex: 'fmsNote',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '备注',
            width: 120,
            dataIndex: 'note',
            render: (text: any) => <YhTootip text={text} />,
        }, {
            title: '打印次数',
            width: 80,
            // fixed: "right",
            align: "center",
            dataIndex: 'printingTimes',
        },
        {
            title: '所属站点',
            width: 250,
            fixed: "right",
            dataIndex: 'shopName',
            render: (text: any) => <YhTootip text={text} style={{ width: 250 }} />,
        },
    ];

    const roleColumns = useMemo(() => {
        //根据可视化配置处理需要展示相关列
        const params = { "code": "saleReturn", "roleCodes": roles };
        return getRoleColumns("saleReturn", "主单", defaultColumns, params);
    }, [roles, dataSource]);
    //点击行选高亮
    const onRow = (record: ISaleRoDetail) => {
        let { id } = record as { id: string }
        let findIndexDesc = descRows.findIndex((item) => (item.id === id))
        let newDescRows = [...descRows];
        let newSelectedRowKeys = [...selectedRowKeys];

        if (findIndexDesc === -1) {
            //统计数据
            newDescRows.push(record)
            setDescRows(newDescRows)
            //表格选中行Key值
            newSelectedRowKeys.push(id);
            setSelectedRowKeys(newSelectedRowKeys);
        } else {
            //统计数据
            newDescRows.splice(findIndexDesc, 1);
            setDescRows(newDescRows);
            //表格选中行Key值
            newSelectedRowKeys.splice(findIndexDesc, 1);
            setSelectedRowKeys(newSelectedRowKeys);
        }
    };
    const onRightClick = (record: ISaleRoDetail) => {
        setDescRows([record])
        setSelectedRowKeys([record.id as string])
    }
    return (
        <Table
            bordered
            style={{ cursor: "pointer" }}
            size={tableSize}
            rowKey={record => record.id as string}
            rowSelection={{
                onChange: (selectedRowKeys: React.Key[], selectedRows: ISaleRoDetail[]) => {
                    setSelectedRowKeys(selectedRowKeys as string[]);
                    setDescRows(selectedRows)
                },
                selectedRowKeys
            }}
            columns={roleColumns}
            dataSource={dataSource}
            pagination={{
                showSizeChanger: true,
                ...pgCfg,
                defaultPageSize: 25,
                pageSizeOptions: ['15', '25', '50', '100', '200', '500'],
                position: ["bottomRight"],
                showTotal: () => `共${pgCfg.total}条`,
                onChange: (pageNum, pageSize) => {
                    loadData({ pageNum, pageSize }).then();
                }
            }}
            onRow={record => ({
                // onClick: () => onRow(record.id as string),
                onClick: () => onRow(record),
                onContextMenu: (e) => {
                    e.preventDefault();
                    onRightClick(record)
                }
            })}
            scroll={{ x: 'max-content', y: 'calc(100vh - 370px)' }}
            summary={() => (
                <Table.Summary fixed>
                    <Table.Summary.Row className="table-total-box">
                        <Table.Summary.Cell index={0}>
                            <div className="title">合计</div>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} />
                        <Table.Summary.Cell index={2} />
                        <Table.Summary.Cell index={3} />
                        <Table.Summary.Cell index={4} />
                        <Table.Summary.Cell index={5} />
                        <Table.Summary.Cell index={6} />
                        <Table.Summary.Cell index={7} />
                        <Table.Summary.Cell index={8} />
                        <Table.Summary.Cell index={9}>
                            <span className="red">{selectedRowKeys.length === 0 ? descTotal.totalNum : rowsDescTotal.totalNum}</span>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={10} />
                        {/* >
                            <span className="red">{formatNum(selectedRowKeys.length === 0 ? descTotal.totalAmount : rowsDescTotal.totalAmount)}</span>
                        </Table.Summary.Cell> */}
                        <Table.Summary.Cell index={11} />
                        {/* >
                            <span className="red">{formatNum(selectedRowKeys.length === 0 ? descTotal.receivableAmount : rowsDescTotal.receivableAmount)}</span>
                        </Table.Summary.Cell> */}
                        <Table.Summary.Cell index={12} />
                        {/* >
                            <span className="red">{formatNum(selectedRowKeys.length === 0 ? descTotal.latestPurPrice : rowsDescTotal.latestPurPrice)}</span>
                        </Table.Summary.Cell> */}
                        <Table.Summary.Cell index={13} />
                        <Table.Summary.Cell index={14} />
                        <Table.Summary.Cell index={15} />
                        <Table.Summary.Cell index={16} />
                        <Table.Summary.Cell index={17} />
                        <Table.Summary.Cell index={18} />
                        <Table.Summary.Cell index={19} />
                        <Table.Summary.Cell index={20} />
                        <Table.Summary.Cell index={21} />
                        <Table.Summary.Cell index={22} />
                        <Table.Summary.Cell index={23} />
                        <Table.Summary.Cell index={24} />
                        <Table.Summary.Cell index={25} />
                        <Table.Summary.Cell index={26} />
                        <Table.Summary.Cell index={27} />
                        <Table.Summary.Cell index={28} />
                        <Table.Summary.Cell index={29} />
                        <Table.Summary.Cell index={30} />
                        <Table.Summary.Cell index={31} />
                        <Table.Summary.Cell index={32} />
                        <Table.Summary.Cell index={33} />
                        <Table.Summary.Cell index={34} />
                        <Table.Summary.Cell index={35} />
                        <Table.Summary.Cell index={36} />
                        <Table.Summary.Cell index={37} />
                        <Table.Summary.Cell index={38} />
                        <Table.Summary.Cell index={39} />
                        <Table.Summary.Cell index={40} />
                        <Table.Summary.Cell index={41} />
                    </Table.Summary.Row>
                </Table.Summary>
            )}
        />
    )
}
